import {
  Archive,
  AlertTriangle,
  CheckCircle,
  Info,
  Loader2,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'


import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * 图标类型
 */
export type IconType = 'delete' | 'archive' | 'warning' | 'info' | 'success' | 'none'

/**
 * 预设配置类型
 */
export type PresetType = 'delete' | 'archive' | 'approve' | 'cancel' | 'restore' | 'deactivate' | 'remove'

/**
 * 确认对话框选项
 */
export interface ConfirmOptions {
  /** 对话框标题 */
  title: string
  /** 对话框描述 */
  description: string
  /** 确认按钮文本，默认 "确认" */
  confirmText?: string
  /** 取消按钮文本，默认 "取消" */
  cancelText?: string
  /** 是否为危险操作（删除等），会使用红色按钮 */
  destructive?: boolean
  /** 批量操作时显示的数量 */
  itemCount?: number
  /** 图标类型 */
  icon?: IconType
}

/**
 * 预设配置
 */
export const PRESETS: Record<PresetType, Partial<ConfirmOptions>> = {
  delete: {
    title: '确认删除',
    confirmText: '删除',
    destructive: true,
    icon: 'delete',
  },
  archive: {
    title: '确认归档',
    confirmText: '归档',
    destructive: false,
    icon: 'archive',
  },
  approve: {
    title: '确认审批',
    confirmText: '批准',
    destructive: false,
    icon: 'success',
  },
  cancel: {
    title: '确认取消',
    confirmText: '取消操作',
    destructive: true,
    icon: 'warning',
  },
  restore: {
    title: '确认恢复',
    confirmText: '恢复',
    destructive: false,
    icon: 'info',
  },
  deactivate: {
    title: '确认停用',
    confirmText: '停用',
    destructive: true,
    icon: 'warning',
  },
  remove: {
    title: '确认移除',
    confirmText: '移除',
    destructive: true,
    icon: 'delete',
  },
}

/**
 * 图标组件映射
 */
const ICONS: Record<IconType, React.ComponentType<{ className?: string }> | null> = {
  delete: Trash2,
  archive: Archive,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle,
  none: null,
}


/**
 * 可复用的确认对话框 Hook
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { confirm, confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()
 *
 *   // 方式1: 使用预设配置（推荐）
 *   const handleDelete = async (id: string) => {
 *     const confirmed = await confirmWithPreset('delete', '确定要删除此记录吗？此操作不可恢复。')
 *     if (confirmed) {
 *       setLoading(true)
 *       try {
 *         await deleteMutation.mutateAsync(id)
 *       } finally {
 *         setLoading(false)
 *       }
 *     }
 *   }
 *
 *   // 方式2: 自定义配置
 *   const handleArchive = async (id: string) => {
 *     const confirmed = await confirm({
 *       title: '确认归档',
 *       description: '归档后项目将不再显示在列表中。',
 *       confirmText: '归档',
 *       icon: 'archive',
 *     })
 *     if (confirmed) {
 *       archiveMutation.mutate(id)
 *     }
 *   }
 *
 *   return (
 *     <>
 *       <button onClick={() => handleDelete('123')}>删除</button>
 *       <ConfirmDialog />
 *     </>
 *   )
 * }
 * ```
 */
export function useConfirmDialog() {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const [loading, setLoadingState] = useState(false)
  const resolveRef = useRef<((value: boolean) => void) | null>(null)

  /**
   * 显示确认对话框
   * @param opts 对话框选项
   * @returns Promise<boolean> 用户点击确认返回 true，取消返回 false
   */
  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts)
    setLoadingState(false)
    setOpen(true)
    return new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  /**
   * 使用预设配置显示确认对话框
   * @param preset 预设类型
   * @param description 描述文本
   * @param overrides 覆盖预设的选项
   * @returns Promise<boolean> 用户点击确认返回 true，取消返回 false
   */
  const confirmWithPreset = useCallback(
    (
      preset: PresetType,
      description: string,
      overrides?: Partial<ConfirmOptions>
    ): Promise<boolean> => {
      const presetConfig = PRESETS[preset]
      return confirm({
        ...presetConfig,
        description,
        ...overrides,
      } as ConfirmOptions)
    },
    [confirm]
  )

  const handleConfirm = useCallback(() => {
    setOpen(false)
    setLoadingState(false)
    resolveRef.current?.(true)
    resolveRef.current = null
  }, [])

  const handleCancel = useCallback(() => {
    if (loading) {return} // 加载中不允许取消
    setOpen(false)
    setLoadingState(false)
    resolveRef.current?.(false)
    resolveRef.current = null
  }, [loading])

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !loading) {
        handleCancel()
      }
    },
    [handleCancel, loading]
  )

  /**
   * 设置 loading 状态
   */
  const setLoading = useCallback((isLoading: boolean) => {
    setLoadingState(isLoading)
  }, [])

  // 键盘快捷键支持
  useEffect(() => {
    if (!open) {return}

    const handleKeyDown = (e: KeyboardEvent) => {
      if (loading) {return} // 加载中不响应键盘

      if (e.key === 'Escape') {
        e.preventDefault()
        handleCancel()
      }
      // Enter 键由 AlertDialog 组件内部处理
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, loading, handleCancel])

  /**
   * 确认对话框组件，需要在 JSX 中渲染
   */
  const ConfirmDialog = useCallback(() => {
    if (!options) {return null}

    const description =
      options.itemCount && options.itemCount > 0
        ? `${options.description}（共 ${options.itemCount} 项）`
        : options.description

    const IconComponent = options.icon ? ICONS[options.icon] : null

    return (
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {IconComponent && (
                <IconComponent
                  className={`h-5 w-5 ${
                    options.destructive ? 'text-destructive' : 'text-muted-foreground'
                  }`}
                />
              )}
              {options.title}
            </AlertDialogTitle>
            <AlertDialogDescription>{description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel} disabled={loading}>
              {options.cancelText || '取消'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={loading}
              className={
                options.destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : undefined
              }
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {options.confirmText || '确认'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )
  }, [open, options, loading, handleConfirm, handleCancel, handleOpenChange])

  return {
    confirm,
    confirmWithPreset,
    ConfirmDialog,
    setLoading,
    isOpen: open,
    isLoading: loading,
  }
}
